import { supabase } from './supabaseClient';

/**
 * Diagnostics function that verifies database access and RLS policies
 * - Checks basic database connectivity
 * - Tests account access permissions
 * - Verifies role assignment
 * - Returns detailed diagnostic information
 */
export const runAccountDiagnostics = async (userId: string): Promise<{
  success: boolean;
  diagnostics: {
    connectionSuccessful: boolean;
    canReadAccounts: boolean;
    canReadRoles: boolean;
    userAccountsFound: number;
    userRolesFound: number;
    accountList: any[];
    rolesDetails: any[];
    errorDetails?: any;
  }
}> => {
  try {
    const diagnostics = {
      connectionSuccessful: false,
      canReadAccounts: false,
      canReadRoles: false,
      userAccountsFound: 0,
      userRolesFound: 0,
      accountList: [],
      rolesDetails: [],
      errorDetails: null
    };
    
    // Check 1: Test basic database connectivity
    const { data: healthData, error: healthError } = await supabase.rpc('heartbeat');
    diagnostics.connectionSuccessful = !healthError && healthData === true;
    
    if (healthError) {
      diagnostics.errorDetails = { ...diagnostics.errorDetails, health: healthError };
      console.error('Database connectivity error:', healthError);
    }
    
    // Check 2: Test RLS for accounts table
    const { data: accountsData, error: accountsError } = await supabase
      .from('accounts')
      .select('id, name, created_at, subscription_tier, subscription_status')
      .eq('owner_id', userId);
    
    diagnostics.canReadAccounts = !accountsError;
    
    if (accountsError) {
      diagnostics.errorDetails = { ...diagnostics.errorDetails, accounts: accountsError };
      console.error('Error reading accounts:', accountsError);
    } else {
      diagnostics.accountList = accountsData || [];
      diagnostics.userAccountsFound = accountsData?.length || 0;
    }
    
    // Check 3: Test role assignments using the view
    const { data: rolesData, error: rolesError } = await supabase
      .from('user_accounts_view')
      .select('*');
    
    diagnostics.canReadRoles = !rolesError;
    
    if (rolesError) {
      diagnostics.errorDetails = { ...diagnostics.errorDetails, roles: rolesError };
      console.error('Error reading roles:', rolesError);
    } else {
      diagnostics.rolesDetails = rolesData || [];
      diagnostics.userRolesFound = rolesData?.length || 0;
    }
    
    const success = diagnostics.connectionSuccessful && 
                    diagnostics.canReadAccounts && 
                    diagnostics.canReadRoles;
    
    // Log the diagnostic attempt, regardless of outcome
    await supabase
      .from('app_usage_logs')
      .insert({
        user_id: userId,
        account_id: diagnostics.accountList[0]?.id || null,
        activity_type: 'account_diagnostics',
        metadata: { 
          success,
          diagnostics: {
            connectionSuccessful: diagnostics.connectionSuccessful,
            canReadAccounts: diagnostics.canReadAccounts,
            canReadRoles: diagnostics.canReadRoles,
            userAccountsFound: diagnostics.userAccountsFound,
            userRolesFound: diagnostics.userRolesFound,
          }
        }
      });
    
    return {
      success,
      diagnostics
    };
  } catch (error) {
    console.error('Exception in account diagnostics:', error);
    return {
      success: false,
      diagnostics: {
        connectionSuccessful: false,
        canReadAccounts: false,
        canReadRoles: false,
        userAccountsFound: 0,
        userRolesFound: 0,
        accountList: [],
        rolesDetails: [],
        errorDetails: error
      }
    };
  }
};

/**
 * Simple function that creates a basic heartbeat RPC
 * to test database connectivity
 */
export const createDatabaseHeartbeatFunction = async (): Promise<boolean> => {
  try {
    // This should be run with admin privileges during deployment
    const { error } = await supabase.rpc('create_heartbeat_function');
    return !error;
  } catch (error) {
    console.error('Error creating heartbeat function:', error);
    return false;
  }
};

/**
 * Creates a user account using the fix-user-account Edge Function
 * This bypasses RLS policies by using the service role
 */
export const forceCreateUserAccount = async (session: any): Promise<{
  success: boolean;
  account?: any;
  error?: string;
}> => {
  if (!session || !session.user) {
    console.error('Invalid session provided to forceCreateUserAccount');
    return { success: false, error: 'Invalid session' };
  }
  
  try {
    const userId = session.user.id;
    const token = session.access_token;
    
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fix-user-account`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error from fix-user-account function:', errorData);
      return { 
        success: false, 
        error: errorData.error || `Server error: ${response.status}`
      };
    }
    
    const data = await response.json();
    return {
      success: data.success,
      account: data.account
    };
  } catch (error) {
    console.error('Exception in forceCreateUserAccount:', error);
    return { 
      success: false, 
      error: error.message || 'Unknown error' 
    };
  }
};

/**
 * Alternative account creation function that works directly with 
 * the database public API
 * - Avoids service role by using simplified RLS policy
 */
export const simplifiedAccountCreation = async (userId: string, email: string, fullName: string): Promise<{
  success: boolean;
  accountId?: string;
  error?: string;
}> => {
  try {
    // 1. First try to create account (this will work if the user doesn't have one)
    const accountName = `${fullName}'s Account (${new Date().toISOString().split('T')[0]})`;
    
    const { data: accountData, error: accountError } = await supabase
      .from('accounts')
      .insert({
        name: accountName,
        owner_id: userId,
        subscription_tier: 'free',
        subscription_status: 'active'
      })
      .select()
      .single();
    
    let accountId: string;
    
    if (accountError) {
      // Check if this is because an account already exists
      const { data: existingAccounts, error: fetchError } = await supabase
        .from('accounts')
        .select('id')
        .eq('owner_id', userId)
        .limit(1);
      
      if (fetchError || !existingAccounts || existingAccounts.length === 0) {
        console.error('Error creating and fetching account:', accountError, fetchError);
        return { 
          success: false, 
          error: 'Failed to create or find account' 
        };
      }
      
      // Use existing account
      accountId = existingAccounts[0].id;
    } else {
      accountId = accountData.id;
    }
    
    // 2. Now attempt to create the role (will work with simplified RLS policy)
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Super-Admin')
      .single();
    
    if (roleError) {
      console.error('Error fetching Super-Admin role:', roleError);
      return { 
        success: false, 
        error: 'Failed to fetch role data' 
      };
    }
    
    // 3. Assign role
    const { error: roleAssignError } = await supabase
      .from('user_account_roles')
      .insert({
        user_id: userId,
        account_id: accountId,
        role_id: roleData.id
      });
    
    // If role assignment fails due to unique constraint, that's fine (already exists)
    if (roleAssignError && !roleAssignError.message?.includes('unique constraint')) {
      console.error('Error assigning role:', roleAssignError);
      return { 
        success: false, 
        error: 'Failed to assign role' 
      };
    }
    
    // 4. Log activity
    await supabase
      .from('app_usage_logs')
      .insert({
        user_id: userId,
        account_id: accountId,
        activity_type: 'simplified_account_creation',
        metadata: { 
          accountCreated: !accountError,
          roleCreated: !roleAssignError
        }
      });
    
    return {
      success: true,
      accountId
    };
  } catch (error) {
    console.error('Exception in simplifiedAccountCreation:', error);
    return { 
      success: false, 
      error: error.message || 'Unknown error'
    };
  }
};

/**
 * Create database helper functions to diagnose and fix RLS issues
 */
export const generateHelperDBFunctions = async (): Promise<boolean> => {
  // This should be run with admin privileges during deployment
  try {
    const { error } = await supabase.rpc('create_helper_functions');
    return !error;
  } catch (error) {
    console.error('Error creating helper functions:', error);
    return false;
  }
};